import Alert from 'components/UI/Alert'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import ToggleButtonGroup from 'components/UI/ToggleButtonGroup'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppSelector } from 'hooks'
import { useCreditDecision } from 'hooks/useCreditDecision'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { creditProgramIDSelector, programSelector } from 'redux/store/slices/decisionSlice'
import { getStatusSelector } from 'redux/store/slices/statusSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { FLOW_STEPS } from 'share/types'
import { ScoringDefinedCreditProgram } from 'share/types/api/credits'
import tw from 'twin.macro'
import { getWsStepStatus, numberWithSpaces } from 'utils'
import { getLoanPurposeText } from 'utils/translations'
import { isScoringOptionsArray } from 'utils/type-guards'

const getMaxPrincipal = (program: ScoringDefinedCreditProgram) => {
  if (isScoringOptionsArray(program)) {
    return Math.max(...program.options.map(option => option.credit_params.credit_sum))
  }
  return program.max_loan_amount
}

const LoanSubprogramChoosing = () => {
  // Tools
  const {
    t,
    i18n: { language }
  } = useTranslation()

  // Selectors
  const selectedProgram = useAppSelector(programSelector)
  const selectedSubProgramId = useAppSelector(creditProgramIDSelector)
  const { current_step } = useAppSelector(getStatusSelector)
  const { APPROVE_PROPERTY_PLEDGES } = useAppSelector(getNotificationsSelector)

  // Custom hooks
  const {
    programs: { isLoading },
    programsByProgramCode,
    prepareSubView,
    selectedSubProgram
  } = useCreditDecision()

  // Computed values
  const isApprovePropertyPledges = useMemo(
    () =>
      getWsStepStatus(APPROVE_PROPERTY_PLEDGES) === 'IN_PROGRESS' ||
      (current_step?.step === FLOW_STEPS.APPROVE_PROPERTY_PLEDGES && current_step.status === 'IN_PROGRESS'),
    [APPROVE_PROPERTY_PLEDGES, current_step]
  )

  return (
    <InfoBlock twStyle={tw`space-y-4`} isContentLoading={isLoading}>
      <Heading as='h2' variant='subtitle'>
        {selectedProgram === 'DAMU'
          ? t('flow-credit-goal')
          : selectedProgram === 'OWN_PROGRAM'
          ? t('alternative-program')
          : null}
      </Heading>
      <ToggleButtonGroup
        value={selectedSubProgramId}
        containerStyles={tw`grid grid-cols-2 gap-x-2 space-x-0 `}
        disabled={isApprovePropertyPledges}
        options={programsByProgramCode.map(program => ({
          value: program.id,
          content: (
            <Fragment>
              {selectedProgram === 'DAMU' ? (
                <Fragment>
                  <Heading as='h3' variant='subtitle' weight='bold'>
                    до {numberWithSpaces(getMaxPrincipal(program)?.toFixed(0))} тг
                  </Heading>
                  <Typography variant='caption'>
                    {t(getLoanPurposeText(program.loan_purpose_verbose), { ns: 'common' })}
                  </Typography>
                </Fragment>
              ) : selectedProgram === 'OWN_PROGRAM' ? (
                !program?.max_loan_amount || program.max_loan_amount <= 0 ? (
                  <Typography variant='body' weight='bold'>
                    {program.with_pledges ? t('with-pledge') : t('without-pledge')}
                  </Typography>
                ) : (
                  <Fragment>
                    <Heading as='h3' variant='subtitle' weight='bold'>
                      до {numberWithSpaces(getMaxPrincipal(program)?.toFixed(0))} тг
                    </Heading>
                    <Typography variant='caption'>
                      {program.with_pledges ? t('with-pledge') : t('without-pledge')}
                    </Typography>
                  </Fragment>
                )
              ) : null}
            </Fragment>
          )
        }))}
        onChange={prepareSubView}
      />
      {selectedSubProgram?.description ? (
        <Alert>
          <Typography variant='subbody' text>
            {language === 'ru' ? selectedSubProgram.description : selectedSubProgram.description_kk}
          </Typography>
        </Alert>
      ) : null}
    </InfoBlock>
  )
}

export default LoanSubprogramChoosing
